import React from 'react';
import { motion } from 'framer-motion';
import { Users, TrendingUp, Building2 } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";

const SlideGTMStrategy = ({ isPreview = false }) => {
    const phases = [
        {
            icon: Users,
            phase: "Phase 1",
            title: "Early Adopters",
            scale: "0 → 10,000 users",
            items: [
                { label: "Target", value: "Young professionals, first-time investors, freelancers" },
                { label: "Channel", value: "Content-led acquisition, founder-led distribution, early-access waitlist" },
                { label: "Goal", value: "High-intent users, rapid feedback, AI training signals" }
            ]
        },
        {
            icon: TrendingUp,
            phase: "Phase 2",
            title: "Conversion & Retention",
            scale: "10k → 100k users",
            items: [
                { label: "Focus", value: "Free-to-paid conversion and habit formation" },
                { label: "Levers", value: "AI-driven nudges, goal-based insights, visible financial improvement" },
                { label: "Metrics", value: "Engagement frequency, decision adoption, paid conversion" }
            ]
        },
        {
            icon: Building2,
            phase: "Phase 3",
            title: "Partnerships & Scale",
            scale: "100k+ users",
            items: [
                { label: "Channels", value: "Payroll platforms, fintech apps, neo-banks, employer benefits" },
                { label: "Model", value: "Embedded AI decision layer (B2B2C)" },
                { label: "Outcome", value: "Lower CAC, higher trust, scalable distribution" }
            ]
        }
    ];

    return (
        <div className="w-full h-full min-h-[100svh] lg:min-h-0 bg-background p-4 sm:p-8 lg:p-16 flex flex-col justify-center relative overflow-hidden">
            {/* Background Grid Texture */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
                style={{ backgroundImage: 'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)', backgroundSize: '40px 40px' }}
            />
            
            <div className="max-w-6xl mx-auto w-full relative z-10">
                {/* Header */}
                <motion.div
                    initial={isPreview ? {} : { opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="mb-8 lg:mb-12"
                >
                    <div className="flex items-center gap-3 mb-3">
                        <span className="text-primary text-xs sm:text-sm font-mono uppercase tracking-[0.2em] border border-primary/20 px-3 py-1 rounded bg-black/50">Growth Playbook</span>
                        <div className="w-8 sm:w-16 h-px bg-primary/20"></div>
                    </div>
                    <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-3">
                        <span className="text-primary">Go-To-Market</span> Strategy
                    </h2>
                    <p className="text-lg sm:text-xl text-muted-foreground">Phased approach to sustainable growth</p>
                </motion.div>

                {/* Three Phase Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8 lg:mb-12">
                    {phases.map((phase, index) => (
                        <motion.div
                            key={index}
                            initial={isPreview ? {} : { opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.15 + 0.2 }}
                        >
                            <Card className="bg-card border-border hover:border-primary/50 transition-all duration-300 h-full group">
                                <CardContent className="p-5 sm:p-6 flex flex-col h-full">
                                    {/* Phase Header */}
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                            <phase.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-primary font-mono text-xs uppercase tracking-widest">{phase.phase}</p>
                                            <h3 className="text-lg sm:text-xl font-bold text-foreground">{phase.title}</h3>
                                        </div>
                                    </div>

                                    {/* Scale Badge */}
                                    <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-3 py-1 mb-5 w-fit">
                                        <span className="text-primary font-mono text-sm font-semibold">{phase.scale}</span>
                                    </div>

                                    {/* Content Items */}
                                    <div className="space-y-4 flex-1">
                                        {phase.items.map((item, i) => (
                                            <div key={i} className="border-l-2 border-border pl-3 group-hover:border-primary/30 transition-colors">
                                                <p className="text-xs text-primary font-medium uppercase tracking-wider mb-1">{item.label}</p>
                                                <p className="text-sm text-muted-foreground leading-relaxed">{item.value}</p>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                {/* Insight Footnote */}
                <motion.div
                    initial={isPreview ? {} : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="text-center"
                >
                    <p className="text-base sm:text-lg text-muted-foreground italic">
                        "ZenCap focuses on <span className="text-foreground font-medium not-italic">outcome-driven adoption</span>, not paid growth, to build trust and long-term retention."
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default SlideGTMStrategy;
